import { Component, type ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

interface Props {
  children: ReactNode;
  label?: string; // what failed, e.g. "the Files module"
}
interface State {
  hasError: boolean;
  message: string;
}

// Catches render/runtime errors in its subtree and shows a friendly fallback
// instead of a blank white screen. Wrap any risky module with this.
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: "" };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, message: error instanceof Error ? error.message : "Something went wrong." };
  }

  componentDidCatch(error: unknown) {
    console.error("[ErrorBoundary]", this.props.label || "", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
          <div className="w-14 h-14 rounded-2xl bg-error-container grid place-items-center mb-4">
            <AlertTriangle className="text-error" size={26} />
          </div>
          <h3 className="text-lg font-bold text-on-surface">Couldn't load {this.props.label || "this section"}</h3>
          <p className="text-sm text-on-surface-variant mt-1 max-w-md">
            Something went wrong while rendering. You can retry, or reload the page.
          </p>
          <p className="text-[11px] text-outline mt-2 max-w-md break-words">{this.state.message}</p>
          <div className="flex gap-3 mt-5">
            <button
              onClick={() => this.setState({ hasError: false, message: "" })}
              className="inline-flex items-center gap-2 border border-outline-variant px-4 py-2 rounded-lg text-sm font-semibold text-on-surface hover:bg-surface-container-low"
            >
              <RotateCcw size={16} /> Retry
            </button>
            <button
              onClick={() => window.location.reload()}
              className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#004b9e]"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
